import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { uploadPhoto } from "../api/photos";
import { ApiError } from "../api/client";
import { dequeueUpload, enqueueUpload, getQueuedUpload, listQueuedIds, toFile } from "../lib/uploadQueue";
import { Button } from "../design-system/Button";
import styles from "./UploadDropzone.module.css";

const NAME_STORAGE_KEY = "mariage_uploader_name";

interface UploadDropzoneProps {
  onUploaded: () => void;
  onUploadingChange?: (uploading: boolean) => void;
}

export function UploadDropzone({ onUploaded, onUploadingChange }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploaderName, setUploaderName] = useState(() => localStorage.getItem(NAME_STORAGE_KEY) ?? "");
  const [status, setStatus] = useState<{ kind: "idle" | "uploading" | "error" | "done"; message?: string; count?: number }>({
    kind: "idle",
  });

  function persistName(name: string) {
    setUploaderName(name);
    localStorage.setItem(NAME_STORAGE_KEY, name);
  }

  // The thank-you card clears itself after a while.
  useEffect(() => {
    if (status.kind !== "done") return;
    const timer = setTimeout(() => setStatus({ kind: "idle" }), 15_000);
    return () => clearTimeout(timer);
  }, [status.kind]);

  // A refresh/close mid-upload cancels the in-flight request outright (no way
  // around that for a plain page reload), so warn the guest instead of
  // silently losing a photo. Best-effort only — iOS Safari mostly ignores
  // this — the IndexedDB queue below is what actually makes a refresh safe.
  useEffect(() => {
    if (status.kind !== "uploading") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status.kind]);

  // Every selected file is written to the IndexedDB queue *before* its own
  // upload is attempted, and only removed once the server confirms it. So if
  // the page reloads mid-batch — refresh, tab kill, iOS Safari not honoring
  // beforeunload, whatever — the file that was mid-upload (and everything
  // already queued before it) isn't lost: this same effect runs on the next
  // mount and resumes whatever is still sitting in the queue.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ids = await listQueuedIds();
      if (!cancelled && ids.length > 0) {
        await processQueue(ids);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume-on-mount only; queued items already carry their own uploaderName
  }, []);

  // IDs are resolved to actual file data one at a time, right before each
  // upload — never all at once. Loading every queued photo's full-size bytes
  // into memory simultaneously (via a bulk read) is what caused real
  // corruption on phones with a large batch: fields coming back empty/wrong
  // after a reload, and the UI stalling before the first upload even started.
  async function uploadQueuedId(id: string): Promise<{ ok: true } | { ok: false; message: string }> {
    const item = await getQueuedUpload(id);
    if (!item) return { ok: true }; // already handled (e.g. concurrent resume), nothing to do

    try {
      await uploadPhoto(toFile(item), item.uploaderName);
      await dequeueUpload(item.id);
      onUploaded();
      return { ok: true };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "échec";
      // A 4xx means the server looked at this exact file and rejected it
      // (wrong type, too big, ...) — retrying it on every future page load
      // would just fail the same way forever, so drop it. Network errors and
      // 5xx are transient: leave it queued to retry next time.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        await dequeueUpload(item.id);
      }
      return { ok: false, message: `${item.fileName} (${message})` };
    }
  }

  async function processQueue(ids: string[]) {
    if (ids.length === 0) return;

    setStatus({ kind: "uploading" });
    onUploadingChange?.(true);

    const failed: string[] = [];
    for (let i = 0; i < ids.length; i++) {
      if (ids.length > 1) {
        setStatus({ kind: "uploading", message: `Envoi ${i + 1}/${ids.length}…` });
      }
      const result = await uploadQueuedId(ids[i]!);
      if (!result.ok) failed.push(result.message);
    }

    onUploadingChange?.(false);

    if (failed.length > 0) {
      setStatus({
        kind: "error",
        message:
          failed.length === ids.length
            ? "Échec de l'envoi, réessaie."
            : `${ids.length - failed.length}/${ids.length} envoyées. Échec pour : ${failed.join(", ")}`,
      });
    } else {
      setStatus({ kind: "done", count: ids.length });
    }
  }

  async function handleFilesSelected(fileList: FileList | null) {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;

    // The whole batch is written to the queue *before any upload starts* -
    // one file at a time (bounded memory, same reason as the resume path
    // above) but fully persisted first. That's what actually makes a refresh
    // safe: if every file waited to be queued until its own turn to upload,
    // a refresh part-way through would silently lose every file that hadn't
    // been reached yet, with no error shown (this happened for real: 18
    // selected, refreshed after 9, only the 10th - the one mid-flight -
    // resumed, and 8 were gone without a trace).
    setStatus({ kind: "uploading", message: files.length > 1 ? `Préparation de ${files.length} photos…` : undefined });
    onUploadingChange?.(true);
    if (inputRef.current) inputRef.current.value = "";

    const name = uploaderName.trim();
    const ids: string[] = [];
    for (let i = 0; i < files.length; i++) {
      if (files.length > 1) {
        setStatus({ kind: "uploading", message: `Préparation ${i + 1}/${files.length}…` });
      }
      const item = await enqueueUpload(files[i]!, name);
      ids.push(item.id);
    }

    await processQueue(ids);
  }

  return (
    <div className={styles.container}>
      <input
        className={styles.nameInput}
        type="text"
        placeholder="Ton prénom (optionnel)"
        value={uploaderName}
        onChange={(e) => persistName(e.target.value)}
        maxLength={100}
      />

      <input
        ref={inputRef}
        className={styles.hiddenInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
        multiple
        onChange={(e) => void handleFilesSelected(e.target.files)}
      />

      <Button onClick={() => inputRef.current?.click()} disabled={status.kind === "uploading"}>
        {status.kind === "uploading" ? (status.message ?? "Envoi en cours…") : "📷 Ajouter des photos"}
      </Button>

      {status.kind === "error" && <p className={`${styles.status} ${styles.statusError}`}>{status.message}</p>}

      {status.kind === "done" && (
        <div className={styles.thanks} role="status">
          <p className={styles.thanksTitle}>
            Merci{uploaderName.trim() ? ` ${uploaderName.trim()}` : ""} ! 💛
          </p>
          <p className={styles.thanksText}>
            {status.count === 1
              ? "Ton souvenir est dans la galerie."
              : `Tes ${status.count} souvenirs sont dans la galerie.`}
          </p>
          <Link to="/livredor" className={styles.thanksLink}>
            📖 Laisse-nous un petit mot dans le livre d'or
          </Link>
        </div>
      )}
    </div>
  );
}
